'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from '@/lib/stripe/client';
import PaymentForm from './PaymentForm';
import type { PaymentIntent } from '@stripe/stripe-js';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  price: number;
  currency: string;
}

export default function CheckoutModal({
  isOpen,
  onClose,
  productName,
  price,
  currency,
}: CheckoutModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [billingInfo, setBillingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    addressLine1: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'GB',
  });
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const router = useRouter();

  const handleBillingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    console.log('=== CHECKOUT MODAL - Creating Payment Intent ===');
    console.log('Using fresh Stripe keys from Integrations tab');
    console.log('Product: LUME001, Price: £38.88');
    console.log('Billing info:', billingInfo);

    try {
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billingInfo }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Failed to create payment intent:', data.error);
        throw new Error(data.error || 'Failed to initialize payment');
      }

      console.log('✅ Payment intent created successfully');
      console.log('Client secret received');
      
      setClientSecret(data.clientSecret);
      setShowPaymentForm(true);
    } catch (err: any) {
      console.error('❌ Error:', err);
      setError(err.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent: PaymentIntent) => {
    console.log('✅ Payment successful:', paymentIntent.id);
    console.log('✅ Redirecting to order confirmation immediately (no database save)');
    setPaymentSuccess(true);
    
    // Generate order number from payment intent ID
    const orderNumber = paymentIntent.id.toUpperCase();
    const amount = 38.88; // LUME001 price
    const email = billingInfo.email;
    
    // Immediate redirect with order details as URL parameters
    setTimeout(() => {
      router.push(`/order-confirmation?orderId=${orderNumber}&amount=${amount}&email=${encodeURIComponent(email)}`);
    }, 1500);
  };

  const handlePaymentError = (errorMessage: string) => {
    console.error('❌ Payment error:', errorMessage);
    setError(errorMessage || 'An error occurred during payment. Please try again.');
    setShowPaymentForm(false);
    setClientSecret(null);
  };

  if (!isOpen) return null;

  const stripePromise = getStripe();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-background rounded-xl sm:rounded-2xl shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 p-2 rounded-full hover:bg-foreground/10 transition-colors touch-manipulation"
        >
          <svg
            className="w-5 h-5 sm:w-6 sm:h-6 text-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h2 className="playfair-header text-2xl sm:text-3xl md:text-4xl text-foreground mb-2">
              Secure Checkout
            </h2>
            <p className="text-sm sm:text-base text-foreground/70">
              {productName} — {currency === 'gbp' ? '£' : '$'}{price.toFixed(2)}
            </p>
          </div>

          {/* Success Message */}
          {paymentSuccess && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-green-600">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-sm sm:text-base font-semibold">Payment Successful!</span>
              </div>
              <p className="mt-2 text-xs sm:text-sm text-foreground/70">
                Thank you for your purchase. You will receive a confirmation email shortly.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-600 text-xs sm:text-sm">{error}</p>
            </div>
          )}

          {/* Payment Form with Stripe Elements */}
          {showPaymentForm && clientSecret && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#8B9D83',
                    colorBackground: '#F5F1E8',
                    colorText: '#3C3C3C',
                    colorDanger: '#EF4444',
                    fontFamily: 'system-ui, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '8px',
                  },
                },
              }}
            >
              <PaymentForm
                clientSecret={clientSecret}
                billingInfo={billingInfo}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </Elements>
          )}

          {/* Billing Form */}
          {!showPaymentForm && !paymentSuccess && (
            <form onSubmit={handleBillingSubmit} className="space-y-3 sm:space-y-4">
              <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-foreground/70 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={billingInfo.firstName}
                    onChange={(e) =>
                      setBillingInfo({ ...billingInfo, firstName: e.target.value })
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-background border border-foreground/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-foreground/70 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={billingInfo.lastName}
                    onChange={(e) =>
                      setBillingInfo({ ...billingInfo, lastName: e.target.value })
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-background border border-foreground/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm sm:text-base"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-foreground/70 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={billingInfo.email}
                  onChange={(e) =>
                    setBillingInfo({ ...billingInfo, email: e.target.value })
                  }
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-background border border-foreground/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-foreground/70 mb-2">
                  Address Line 1 *
                </label>
                <input
                  type="text"
                  required
                  value={billingInfo.addressLine1}
                  onChange={(e) =>
                    setBillingInfo({ ...billingInfo, addressLine1: e.target.value })
                  }
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-background border border-foreground/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm sm:text-base"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-foreground/70 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    required
                    value={billingInfo.city}
                    onChange={(e) =>
                      setBillingInfo({ ...billingInfo, city: e.target.value })
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-background border border-foreground/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-foreground/70 mb-2">
                    State/County *
                  </label>
                  <input
                    type="text"
                    required
                    value={billingInfo.state}
                    onChange={(e) =>
                      setBillingInfo({ ...billingInfo, state: e.target.value })
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-background border border-foreground/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm sm:text-base"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-foreground/70 mb-2">
                    Postal Code *
                  </label>
                  <input
                    type="text"
                    required
                    value={billingInfo.postalCode}
                    onChange={(e) =>
                      setBillingInfo({ ...billingInfo, postalCode: e.target.value })
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-background border border-foreground/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-foreground/70 mb-2">
                    Country *
                  </label>
                  <select
                    required
                    value={billingInfo.country}
                    onChange={(e) =>
                      setBillingInfo({ ...billingInfo, country: e.target.value })
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-background border border-foreground/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm sm:text-base"
                  >
                    <option value="GB">United Kingdom</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="AU">Australia</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-6 py-3 bg-primary text-primary-foreground mono-data text-sm uppercase tracking-wider rounded-lg hover:bg-primary/90 transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'PROCESSING...' : 'CONTINUE TO PAYMENT'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}